import React from 'react';

import { Tooltip } from '@mui/material';
import styled from 'styled-components';

export type FunnelItemProps = {
  first?: boolean;
  hidden?: boolean;
  id: string;
  last?: boolean;
  nextPercentage?: number;
  percentage?: number;
  prevPercentage?: number;
  stat?: number;
  title: string;
  tooltip?: string;
};

export function FunnelItem({
  title,
  tooltip,
  stat,
  hidden = false,
  percentage,
  prevPercentage = 1,
  nextPercentage = 1,
  last = false,
  first = false,
  id,
}: FunnelItemProps) {
  const BAR_MAX_HEIGHT = 390;
  const BAR_HEIGHT = BAR_MAX_HEIGHT * ((percentage ?? 0) * 0.01);
  const PREV_BAR_HEIGHT = BAR_MAX_HEIGHT * (prevPercentage * 0.01);
  const NEXT_BAR_HEIGHT = BAR_MAX_HEIGHT * (nextPercentage * 0.01);
  const SLOPE_HEIGHT_LEFT = PREV_BAR_HEIGHT - BAR_HEIGHT;
  const SLOPE_HEIGHT_RIGHT = NEXT_BAR_HEIGHT - BAR_HEIGHT;
  const SLOPE_OFFSET_LEFT = BAR_MAX_HEIGHT - PREV_BAR_HEIGHT;
  const SLOPE_OFFSET_RIGHT = BAR_MAX_HEIGHT - NEXT_BAR_HEIGHT;

  return (
    <FunnelCell $hidden={hidden} $last={last}>
      <FunnelTitle>
        <CardStatText>{stat || '--'}</CardStatText>
        <Tooltip arrow placement='right' title={tooltip}>
          <CardDescriptorText>{title}</CardDescriptorText>
        </Tooltip>
        <PercentagePill>{percentage?.toFixed(0)}%</PercentagePill>
      </FunnelTitle>

      <BarContainer $height={BAR_MAX_HEIGHT}>
        {!first && SLOPE_HEIGHT_LEFT > 0 && (
          <FunnelBarSlopeLeft
            $height={SLOPE_HEIGHT_LEFT}
            $offset={SLOPE_OFFSET_LEFT}
            data-testid={`funnel-bar-slope-left-${String(id)}`}
          />
        )}
        <FunnelBar $height={BAR_HEIGHT} data-testid={`funnel-bar-${String(id)}`} />
        <FunnelBarBG $height={BAR_HEIGHT} $isFirst={first} $isLast={last} />
        {!last && SLOPE_HEIGHT_RIGHT > 0 && (
          <FunnelBarSlopeRight
            $height={SLOPE_HEIGHT_RIGHT}
            $offset={SLOPE_OFFSET_RIGHT}
            data-testid={`funnel-bar-slope-right-${String(id)}`}
          />
        )}
      </BarContainer>
    </FunnelCell>
  );
}

const FunnelTitle = styled.div`
  padding: var(--sizes-60) var(--sizes-40);
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const PercentagePill = styled.div`
  background-color: var(--color-gray-20);
  border-radius: 10px;
  padding: var(--sizes-10) var(--sizes-20);
  font: var(--font-extra-small-12pt-semibold);
  color: var(--color-gray-80);
  position: absolute;
  top: 24px;
  right: var(--sizes-40);
`;

const BarContainer = styled.div<{ $height: number }>`
  position: relative;
  width: 100%;
  min-height: ${(props) => props.$height}px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const FunnelBarSlopeLeft = styled.div<{ $height: number; $offset: number }>`
  top: ${(props) => props.$offset}px;
  width: 22%;
  height: ${(props) => props.$height}px;
  background: linear-gradient(to bottom left, #fff 0%, #fff 50%, #ecf8fb 50%, #ecf8fb 100%);
  position: absolute;
  z-index: 1;
  left: 0;
`;

const FunnelBarSlopeRight = styled.div<{ $height: number; $offset: number }>`
  top: ${(props) => props.$offset}px;
  width: 22%;
  height: ${(props) => props.$height}px;
  background: linear-gradient(to bottom right, #fff 0%, #fff 50%, #ecf8fb 50%, #ecf8fb 100%);
  position: absolute;
  z-index: 1;
  right: 0;
`;

const FunnelBar = styled.div<{ $height: number }>`
  background: var(--color-teal-50);
  height: ${(props) => Math.max(props.$height, 1)}px;
  transition: height 0.5s ease;
  width: 60%;
  border-radius: 10px 10px 0px 0px;
  z-index: 2;
`;

const FunnelBarBG = styled.div<{ $height: number; $isFirst: boolean; $isLast: boolean }>`
  background: var(--color-teal-10);
  height: ${(props) => Math.max(props.$height, 1)}px;
  width: ${(props) => (props.$isFirst || props.$isLast ? '60%' : '100%')};
  position: absolute;
  z-index: 1;
  left: ${(props) => (props.$isFirst ? 'unset' : '0')};
  right: ${(props) => (props.$isLast ? 'unset' : '0')};
`;

const FunnelCell = styled.div<{ $hidden: boolean; $last: boolean }>`
  display: ${(props) => (props.$hidden ? 'none' : 'flex')};
  flex-direction: column;
  border-width: 1px ${(props) => (props.$last ? '0' : '1')}px 0px 0px;
  border-top-style: solid;
  border-right-style: dashed;
  border-color: var(--color-gray-20);
  width: 100%;
  height: 500px;
  align-items: center;
  justify-content: space-between;
`;

const CardStatText = styled.div`
  font: var(--font-headings-heading-2);
  margin-bottom: var(--sizes-20);
`;

const CardDescriptorText = styled.span`
  font: var(--font-extra-small-12pt-normal);
  width: fit-content;
  display: block;
  border-bottom: 1px dashed var(--color-gray-90);
`;
